import { AxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import { useCallback, useContext } from 'react'
import useSWR from 'swr'
import { FetcherType, RolodexMetadataField } from 'types'

/**
 * A hook for fetching from the rolodex_metadata endpoints.
 * @param metadataField The specific piece of metadata to fetch.
 */
export const useRolodexMetadata = (
  metadataField: RolodexMetadataField
): {
  /** The data returned from rolodex */
  data: string | undefined
  /** Any errors returned from SWR */
  error: AxiosError
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
} => {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { apiURL } = useContext(RolodexContext)

  const fetcher = useCallback(
    async (endpoint: string) => {
      return authorizedAxios
        .get(`${apiURL}/api/v2/${endpoint}`)
        .then((resp) => resp.data)
    },
    [authorizedAxios, apiURL]
  ) as FetcherType<string>

  // SWR will not make a request at all if we're not authenticated yet
  // isLoading and isValidating will both be false in this case
  const { data, error, isLoading, isValidating } = useSWR<string>(
    isAuthenticated ? `rolodex_metadata/${metadataField}` : null,
    fetcher
  )
  return { data, error, isLoading, isValidating }
}
